// src/web_pages/RestaurantPage/RestDetails/BuisRating.js
import '../../../RatingReviews.css';
import {
    SentimentVeryDissatisfied,
    SentimentDissatisfied,
    SentimentNeutral,
    SentimentSatisfied,
    SentimentVerySatisfied,
    Close,
    ExpandMore
} from '@mui/icons-material';
import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../../ContextApi/context";
import utilsRatings from "../../../_Utils/utilsRatings";
import { createPortal } from 'react-dom';

const BuisRating = (props) => {
    const [user] = useContext(UserContext);
    const [averageRating, setAverageRating] = useState(props.rating || 0);
    const [userRating, setUserRating] = useState(0);
    const [userComment, setUserComment] = useState("");
    const [hoverRating, setHoverRating] = useState(0);
    const [totalRatings, setTotalRatings] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [showReviewsPopup, setShowReviewsPopup] = useState(false);
    const [allReviews, setAllReviews] = useState([]);
    const [loadingReviews, setLoadingReviews] = useState(false);
    const [showAllRatings, setShowAllRatings] = useState(false);
    const [selectedFilterRating, setSelectedFilterRating] = useState(null);
    const [showReviewsWithComments, setShowReviewsWithComments] = useState(false);

    useEffect(() => {
        if (props.restId) {
            loadRatings();
        }
    }, [props.restId, user]);

    const loadRatings = async () => {
        try {
            // Load restaurant average rating
            const restRating = await utilsRatings.getRestaurantAverageRating(props.restId);
            if (restRating) {
                setAverageRating(restRating.averageRating || 0);
                setTotalRatings(restRating.totalRatings || 0);
            }

            // Load user's rating if logged in
            if (user && user.usrmsg !== "noUser") {
                const userRatingData = await utilsRatings.getUserRestaurantRating(props.restId);
                if (userRatingData && userRatingData.rating) {
                    setUserRating(userRatingData.rating.rating);
                    setUserComment(userRatingData.rating.comment || "");
                }
            }
        } catch (error) {
            console.error("Error loading ratings:", error);
        }
    };

    const loadAllReviews = async () => {
        try {
            setLoadingReviews(true);
            const reviewsData = await utilsRatings.getAllRestaurantReviews(props.restId);
            setAllReviews(reviewsData?.reviews || []);
        } catch (error) {
            console.error("Error loading reviews:", error);
        } finally {
            setLoadingReviews(false);
        }
    };

    const sortReviewsBySelectedRating = (reviews) => {
        if (!selectedFilterRating) return reviews;

        // Create a circular sort based on selected rating
        // Order: selected -> higher -> lower (circular)
        const sorted = [...reviews].sort((a, b) => {
            const getDistance = (rating) => {
                if (rating === selectedFilterRating) return 0; // Selected comes first
                if (rating > selectedFilterRating) return rating - selectedFilterRating; // Higher ratings next
                return (5 - selectedFilterRating) + rating + 1; // Lower ratings last (wrap around)
            };

            return getDistance(a.rating) - getDistance(b.rating);
        });

        return sorted;
    };

    const openRatingPopup = () => {
        if (!user || user.usrmsg === "noUser") {
            setMessage("יש להתחבר כדי לדרג");
            setTimeout(() => setMessage(""), 3000);
            return;
        }
        setShowPopup(true);
    };

    const openReviewsPopup = () => {
        setShowReviewsPopup(true);
        setShowAllRatings(false);
        setShowReviewsWithComments(false);
        setSelectedFilterRating(null);
        loadAllReviews();
    };

    const handleRatingClick = async (rating) => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);
            const result = await utilsRatings.submitUserRating(props.restId, rating, userComment);

            if (result && result.success) {
                setUserRating(rating);
                setMessage("הדירוג נשמר בהצלחה!");
                // Reload ratings to get updated average
                await loadRatings();
                setTimeout(() => {
                    setShowPopup(false);
                    setMessage("");
                }, 1500);
            } else {
                setMessage("שגיאה בשמירת הדירוג");
                setTimeout(() => setMessage(""), 3000);
            }
        } catch (error) {
            console.error("Error submitting rating:", error);
            setMessage("שגיאה בשמירת הדירוג");
            setTimeout(() => setMessage(""), 3000);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Get smiley icon based on rating level
    const getSmileyIcon = (level, isActive = false, isInteractive = false) => {
        const className = `rating-smiley ${isActive ? 'active' : ''} ${isInteractive ? 'interactive' : ''}`;

        // Define colors for each level
        const colors = {
            1: '#f44336', // Red
            2: '#ff9800', // Orange
            3: '#ffc107', // Yellow
            4: '#8bc34a', // Light Green
            5: '#4caf50'  // Green
        };

        const style = isActive ? { color: colors[level] } : {};

        switch (level) {
            case 1:
                return <SentimentVeryDissatisfied className={className} style={style} />;
            case 2:
                return <SentimentDissatisfied className={className} style={style} />;
            case 3:
                return <SentimentNeutral className={className} style={style} />;
            case 4:
                return <SentimentSatisfied className={className} style={style} />;
            case 5:
                return <SentimentVerySatisfied className={className} style={style} />;
            default:
                return <SentimentNeutral className={className} style={style} />;
        }
    };

    const renderSmileys = (rating, isInteractive = false) => {
        const smileys = [];
        const displayRating = isInteractive ? (hoverRating || rating) : Math.round(rating);

        for (let i = 1; i <= 5; i++) {
            const isActive = i === displayRating;
            smileys.push(
                <div
                    key={i}
                    className="smiley-wrapper"
                    onClick={() => isInteractive && setUserRating(i)} // <-- רק שומר, לא שולח!
                    onMouseEnter={() => isInteractive && setHoverRating(i)}
                    onMouseLeave={() => isInteractive && setHoverRating(0)}
                >
                    {getSmileyIcon(i, isActive, isInteractive)}
                </div>
            );
        }
        return smileys;
    };

    // Get the main smiley icon for display (shows the average rating smiley)
    const getMainSmiley = (rating) => {
        const roundedRating = Math.round(rating);
        return getSmileyIcon(roundedRating > 0 ? roundedRating : 4, true, false);
    };

    const getRatingText = (rating) => {
        switch (rating) {
            case 1: return "גרוע מאוד";
            case 2: return "לא טוב";
            case 3: return "בסדר";
            case 4: return "טוב";
            case 5: return "מעולה";
            default: return "";
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString('he-IL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // If no restId provided, don't show interactive rating
    if (!props.restId) {
        return <div className="rating"></div>;
    }

    return (
        <>
            <div className="rating">
                {/* <div
                    className="rating-display clickable onRight"
                    onClick={openReviewsPopup}
                    title="לחץ לצפייה בכל הדירוגים"
                >
                    <div className="main-smiley-container">
                        {getMainSmiley(averageRating)}
                    </div>
                    <span className="rating-text">
                        {parseFloat(averageRating) > 0 ? averageRating.toFixed(1) : ""}
                        {totalRatings > 0 && ` (${totalRatings})`}
                    </span>

                </div> */}
                {user && user.usrmsg !== "noUser" && (
                    <div className="rating-button onRight" onClick={openRatingPopup}>
                        {userRating > 0 ? <div style={{ direction: 'rtl' }}><span>הדירוג שלך: </span>{getMainSmiley(userRating)}</div> : "דרג את העסק"}
                    </div>
                )}



                {message && !showPopup && (
                    <div className="rating-message">
                        {message}
                    </div>
                )}
            </div>

            {/* Rating Popup */}
            {showPopup && createPortal(
                <div className="rating-popup-overlay" onClick={() => setShowPopup(false)}>
                    <div className="rating-popup" onClick={(e) => e.stopPropagation()}>
                        <div className="rating-popup-header">
                            <h3>דרג את העסק</h3>
                            <button className="rating-popup-close" onClick={() => setShowPopup(false)}>
                                <Close />
                            </button>
                        </div>

                        <div className="rating-popup-content">
                            <p className="rating-popup-text">
                                {hoverRating > 0 ? getRatingText(hoverRating) :
                                    userRating > 0 ? `הדירוג הנוכחי שלך: ${getRatingText(userRating)}` :
                                        "בחר דירוג"}
                            </p>

                            <div className="rating-popup-smileys">
                                {renderSmileys(userRating, true)}
                            </div>

                            <div className="rating-comment-section">
                                <label className="rating-comment-label">הוסף הערה (אופציונלי)</label>
                                <textarea
                                    className="rating-comment-input"
                                    value={userComment}
                                    onChange={(e) => setUserComment(e.target.value)}
                                    placeholder="ספר לנו על החוויה שלך..."
                                    maxLength={500}
                                    rows={4}
                                />
                                <div className="rating-comment-counter">
                                    {userComment.length}/500
                                </div>
                            </div>


                            {/* כפתור שמור */}
                            <button
                                className="rating-save-button"
                                onClick={() => handleRatingClick(userRating)}
                                disabled={isSubmitting || userRating === 0}
                            >
                                {isSubmitting ? "שומר..." : "שמור דירוג"}
                            </button>

                            {message && (
                                <div className="rating-popup-message">
                                    {message}
                                </div>
                            )}

                        </div>
                    </div>
                </div>
                , document.body
            )}

            {/* Reviews List Popup */}
            {showReviewsPopup && createPortal(
                <div className="rating-popup-overlay" onClick={() => setShowReviewsPopup(false)}>
                    <div className="reviews-popup" onClick={(e) => e.stopPropagation()}>
                        <div className="rating-popup-header">
                            <div className="reviews-average-section" style={{ margin: "0 auto" }}>
                                <div className="main-smiley-container">
                                    {getMainSmiley(averageRating)}
                                </div>
                                <div className="reviews-average-text">
                                    <span className="reviews-average-score">
                                        {averageRating > 0 ? averageRating.toFixed(1) : "0.0"}
                                    </span>
                                    {totalRatings > 0 && <span className="reviews-average-count">({totalRatings})</span>}
                                </div>
                            </div>
                            <button className="rating-popup-close" onClick={() => setShowReviewsPopup(false)}>
                                <Close />
                            </button>
                        </div>

                        <div className="reviews-popup-content">
                            {loadingReviews ? (
                                <div className="reviews-loading">טוען דירוגים...</div>
                            ) : (
                                <>
                                    {/* Tab Buttons */}
                                    <div className="reviews-tabs">
                                        <button
                                            className={`reviews-tab-button ${showReviewsWithComments ? 'active' : ''}`}
                                            onClick={() => {
                                                if (showReviewsWithComments) {
                                                    setShowReviewsWithComments(false);
                                                } else {
                                                    setShowReviewsWithComments(true);
                                                    setShowAllRatings(false);
                                                }
                                            }}
                                        >
                                            חוות דעת ({allReviews.filter(review => review.comment && review.comment.trim() !== "").length})
                                        </button>

                                        <button
                                            className={`reviews-tab-button ${showAllRatings ? 'active' : ''}`}
                                            onClick={() => {
                                                if (showAllRatings) {
                                                    setShowAllRatings(false);
                                                } else {
                                                    setShowAllRatings(true);
                                                    setShowReviewsWithComments(false);
                                                }
                                            }}
                                        >
                                            כל הדירוגים ({totalRatings})
                                        </button>
                                    </div>

                                    {/* Content based on active tab */}
                                    {(showReviewsWithComments || showAllRatings) && (
                                        <>
                                            {/* Smiley Filter Controls */}
                                            <div className="reviews-filter-controls">
                                                <span className="filter-label">סנן לפי דירוג:</span>
                                                <div className="smiley-filters">
                                                    <div
                                                        className={`smiley-filter ${selectedFilterRating === null ? 'active' : ''}`}
                                                        onClick={() => setSelectedFilterRating(null)}
                                                    >
                                                        <span className="filter-all-text">הכל</span>
                                                    </div>
                                                    {[5, 4, 3, 2, 1].map(rating => (
                                                        <div
                                                            key={rating}
                                                            className={`smiley-filter ${selectedFilterRating === rating ? 'active' : ''}`}
                                                            onClick={() => setSelectedFilterRating(rating)}
                                                        >
                                                            {getSmileyIcon(rating, true, false)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Reviews List */}
                                            <div className="reviews-list">
                                                {showReviewsWithComments ? (
                                                    // Show only reviews with comments
                                                    sortReviewsBySelectedRating(
                                                        allReviews.filter(review => review.comment && review.comment.trim() !== "")
                                                    ).map((review, index) => (
                                                        <div key={index} className="review-item">
                                                            <div className="review-header">
                                                                <div className="review-user-info">
                                                                    <span className="review-user-name">
                                                                        {review.userName || "משתמש אנונימי"}
                                                                    </span>
                                                                    <span className="review-date">
                                                                        {formatDate(review.createdAt)}
                                                                    </span>
                                                                </div>
                                                                <div className="review-rating">
                                                                    {getSmileyIcon(review.rating, true, false)}
                                                                    <span className="review-rating-text">
                                                                        {getRatingText(review.rating)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="review-comment">
                                                                {review.comment}
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    // Show all ratings
                                                    sortReviewsBySelectedRating(allReviews).map((review, index) => (
                                                        <div key={index} className="review-item">
                                                            <div className="review-header">
                                                                <div className="review-user-info">
                                                                    <span className="review-user-name">
                                                                        {review.userName || "משתמש אנונימי"}
                                                                    </span>
                                                                    <span className="review-date">
                                                                        {formatDate(review.createdAt)}
                                                                    </span>
                                                                </div>
                                                                <div className="review-rating">
                                                                    {getSmileyIcon(review.rating, true, false)}
                                                                    <span className="review-rating-text">
                                                                        {getRatingText(review.rating)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            {review.comment && review.comment.trim() !== "" && (
                                                                <div className="review-comment">
                                                                    {review.comment}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
                , document.body
            )}

        </>
    );
}

export default BuisRating;